/**
 * GamePlayer - 게임 캔버스 공용 컴포넌트 (상단바는 호스트 앱에서 렌더)
 *
 * 책임:
 * - gameKey로 게임 모듈을 lazy 로드
 * - Suspense로 감싸 캔버스 영역에 렌더
 * - penguin-game 캔버스 포지셔닝/크기 조정 스타일 주입
 *
 * 호스트 앱이 렌더하는 것:
 * - 상단바(뒤로/타이틀/사운드)
 * - 로딩 fallback(loadingFallback prop)
 * - Unknown game 에러 화면(getGameEntry로 사전 가드)
 */
import { Suspense, lazy, useMemo, type ReactNode } from 'react'
import type { GameApiAdapter } from '../types/game-api'
import { getGameEntry } from '../registry'

const noopApi: GameApiAdapter = {
  onGameCompleted: (gameName, subType, score, time, callback) => {
    console.log('[GamePlayer] onComplete:', { gameName, subType, score, time })
    callback?.({ score, time, user: { nickname: 'Guest' } })
  },
  onScoreUpdate: (gameName, subType, score, time, callback) => {
    console.log('[GamePlayer] onScoreUpdate:', { gameName, subType, score, time })
    callback?.({ score, time, user: { nickname: 'Guest' } })
  },
  loadRanking: (_gameName, _subType, callback) => {
    console.log('[GamePlayer] loadRanking (mock)')
    callback({ ranking: [], my: null })
  },
  loadState: (_gameName, _subType, callback) => {
    console.log('[GamePlayer] loadState (mock)')
    callback({})
  },
  saveState: (_gameName, _subType, data, callback) => {
    console.log('[GamePlayer] saveState (mock):', data)
    callback?.(data)
  },
}

const noopClose = () => {}

export interface GamePlayerProps {
  gameKey: string
  user?: { nickname: string; id?: string } | null
  api?: GameApiAdapter
  isSoundOn?: boolean
  loadingFallback?: ReactNode
  onClose?: () => void
}

export function GamePlayer({
  gameKey,
  user,
  api,
  isSoundOn = true,
  loadingFallback = null,
  onClose,
}: GamePlayerProps) {
  const entry = useMemo(() => getGameEntry(gameKey), [gameKey])
  const adapter = api ?? noopApi
  const effectiveUser = user ?? { nickname: 'Guest' }
  const close = onClose ?? noopClose

  const GameComponent = useMemo(
    () => (entry ? lazy(entry.loader) : null),
    [entry]
  )

  if (!entry || !GameComponent) return null

  return (
    <div className="relative flex-1 overflow-hidden h-full">
      <style>{`
        #penguin-game {
          position: absolute !important;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }
        #penguin-game canvas {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }
      `}</style>
      <Suspense fallback={loadingFallback}>
        <GameComponent
          user={effectiveUser}
          onComplete={adapter.onGameCompleted}
          onScoreUpdate={adapter.onScoreUpdate}
          loadRanking={adapter.loadRanking}
          loadState={adapter.loadState}
          saveState={adapter.saveState}
          isSoundOn={isSoundOn}
          isFullscreen={false}
          onHandleHome={close}
          goHome={false}
          onClose={close}
        />
      </Suspense>
    </div>
  )
}
